import { useState, useEffect } from 'react';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const navLinks = [
    { label: 'Accueil', href: '#hero' },
    {
        label: 'Activités',
        href: '#activites',
        children: [
            { label: 'Consommables & Fournitures de Bureau', href: '#consommables' },
            { label: 'EPI & Hygiène', href: '#epi' },
            { label: 'Ergonomie & Formation', href: '#ergonomie' },
            { label: 'Étiquettes', href: '#etiquettes' },
            { label: 'Personnalisation', href: '#personnalisation' },
            { label: 'Outillage', href: '#outillages' },
            { label: 'Ameublement', href: '#ameublement' },
        ],
    },
    { label: 'Notre ADN', href: '#adn' },
    { label: 'Engagements', href: '#rse' },
    { label: 'L\'équipe', href: '#equipe' },
    { label: 'Contact', href: '#contact' },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollTo = (href: string) => {
        setMobileOpen(false);
        setActiveDropdown(null);
        const el = document.querySelector(href);
        if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 72;
            window.scrollTo({ top, behavior: 'smooth' });
        }
    };

    return (
        <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white border-b border-gray-100' : 'bg-transparent border-b border-white/10'}`}>

            {/* Top bar — contacts (masqué sur mobile) */}
            <div className={`transition-all duration-500 overflow-hidden hidden sm:block ${scrolled ? 'sm:h-0 sm:opacity-0' : 'sm:h-8 sm:opacity-100'}`}>
                <div className="max-w-7xl mx-auto px-6 lg:px-8 h-full flex items-center justify-end gap-6">
                    <a href="tel:0329292774" className="text-white/50 hover:text-white text-2xs tracking-widest font-sans transition-colors duration-200">03 29 29 27 74</a>
                    <span className="text-white/20 text-2xs">·</span>
                    <a href="mailto:contact@segecdirect.fr" className="text-white/50 hover:text-white text-2xs tracking-widest font-sans transition-colors duration-200">contact@segecdirect.fr</a>
                    <span className="text-white/20 text-2xs">·</span>
                    <a href="https://fr.linkedin.com/company/segecdirect" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white transition-colors duration-200" aria-label="LinkedIn SEGEC Direct">
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    </a>
                </div>
            </div>

            {/* Main nav */}
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">

                    {/* Logo */}
                    <button onClick={() => scrollTo('#hero')} className="flex-shrink-0 group">
                        <img src="/logo.svg" alt="Segec Direct" className={`h-14 sm:h-16 w-auto transition-all duration-300 ${scrolled ? '' : 'brightness-0 invert'} group-hover:opacity-80`} />
                    </button>

                    {/* Desktop nav */}
                    <nav className="hidden lg:flex items-center gap-1">
                        {navLinks.map((link) =>
                            link.children ? (
                                <div key={link.label} className="relative" onMouseEnter={() => setActiveDropdown(link.label)} onMouseLeave={() => setActiveDropdown(null)}>
                                    <button className={`flex items-center gap-1 px-4 py-2 text-xs tracking-widest font-sans font-medium uppercase transition-colors duration-200 ${scrolled ? 'text-gray-500 hover:text-segec-ink' : 'text-white/60 hover:text-white'}`}>
                                        {link.label}
                                        <ChevronDownIcon className={`w-3 h-3 transition-transform duration-200 ${activeDropdown === link.label ? 'rotate-180' : ''}`} />
                                    </button>
                                    <div className={`absolute top-full left-0 pt-2 transition-all duration-200 ${activeDropdown === link.label ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-1 pointer-events-none'}`}>
                                        <div className="bg-white border border-gray-100 shadow-xl rounded-xl py-2 w-56 overflow-hidden">
                                            {link.children.map((child) => (
                                                <button key={child.label} onClick={() => scrollTo(child.href)} className="w-full text-left px-5 py-3 text-xs text-gray-500 hover:text-segec-ink hover:bg-segec-stone font-sans tracking-wide transition-colors duration-150 flex items-center gap-3 group/item">
                                                    <span className="w-4 h-px bg-segec-orange scale-x-0 group-hover/item:scale-x-100 transition-transform duration-200 origin-left" />
                                                    {child.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <button key={link.label} onClick={() => scrollTo(link.href)} className={`px-4 py-2 text-xs tracking-widest font-sans font-medium uppercase transition-colors duration-200 ${scrolled ? 'text-gray-500 hover:text-segec-ink' : 'text-white/60 hover:text-white'}`}>
                                    {link.label}
                                </button>
                            )
                        )}
                    </nav>

                    {/* CTA */}
                    <div className="hidden lg:flex items-center">
                        <a href="mailto:contact@segecdirect.fr" className={`text-xs font-sans font-semibold tracking-widest uppercase px-5 py-2.5 rounded-full border transition-all duration-300 hover:-translate-y-px ${scrolled ? 'border-segec-ink text-segec-ink hover:bg-segec-ink hover:text-white' : 'border-white/40 text-white hover:bg-white hover:text-segec-ink'}`}>
                            Devis gratuit
                        </a>
                    </div>

                    {/* Burger */}
                    <button onClick={() => setMobileOpen(!mobileOpen)} className={`lg:hidden p-2 transition-colors ${scrolled ? 'text-segec-ink' : 'text-white'}`}>
                        {mobileOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
                    </button>

                </div>
            </div>

            {/* Mobile menu */}
            <div className={`lg:hidden transition-all duration-300 overflow-hidden ${mobileOpen ? 'max-h-screen bg-white border-t border-gray-100' : 'max-h-0'}`}>
                <nav className="px-6 py-4 flex flex-col">
                    {navLinks.map((link) => (
                        <div key={link.label}>
                            <button
                                onClick={() => {
                                    if (link.children) setActiveDropdown(activeDropdown === link.label ? null : link.label);
                                    else scrollTo(link.href);
                                }}
                                className="w-full flex items-center justify-between py-3 border-b border-gray-50 text-xs font-semibold uppercase tracking-widest text-gray-600 hover:text-segec-ink transition-colors"
                            >
                                {link.label}
                                {link.children && (
                                    <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${activeDropdown === link.label ? 'rotate-180' : ''}`} />
                                )}
                            </button>
                            {link.children && activeDropdown === link.label && (
                                <div className="pl-4 py-1">
                                    {link.children.map((child) => (
                                        <button key={child.label} onClick={() => scrollTo(child.href)} className="w-full text-left py-2.5 text-xs text-gray-400 hover:text-segec-green transition-colors tracking-wide">
                                            {child.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                    <a href="mailto:contact@segecdirect.fr" className="mt-5 text-center text-xs font-semibold uppercase tracking-widest py-3 border border-segec-ink text-segec-ink rounded-full hover:bg-segec-ink hover:text-white transition-all duration-200">
                        Demander un devis
                    </a>
                </nav>
            </div>
        </header>
    );
}
